import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, ShoppingCart, Check, ShieldCheck } from "lucide-react";
import ProductVial from "@/components/ProductVial";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { getProductImage } from "@/constants/productImages";
import { toast } from "sonner";

export default function ProductCard({ product, categorySlug }) {
  const { addItem } = useCart();
  const inStock = product.stock_quantity > 0;
  const [added, setAdded] = useState(false);
  const [showQty, setShowQty] = useState(false);
  const [qty, setQty] = useState(1);
  const realImage = getProductImage(product.slug, product);

  const openQty = (e) => {
    e.preventDefault();
    setQty(1);
    setShowQty(true);
  };

  const changeQty = (e, delta) => {
    e.preventDefault();
    setQty((q) => Math.min(Math.max(1, q + delta), product.stock_quantity));
  };

  const confirmAdd = (e) => {
    e.preventDefault();
    addItem(product, qty);
    toast.success(`${qty} x ${product.name} added to cart`);
    setShowQty(false);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  const cancelQty = (e) => {
    e.preventDefault();
    setShowQty(false);
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      className="group relative bg-white border border-slate-200 rounded-2xl p-5 shadow-premium-sm hover:shadow-blue-glow hover:border-blue-200 card-lift flex flex-col"
      data-testid={`product-card-${product.slug}`}
    >
      <div className={`relative flex items-center justify-center h-48 ${realImage ? "bg-white border border-slate-100" : "bg-gradient-to-b from-blue-50/60 to-slate-50"} rounded-xl mb-4 overflow-hidden`}>
        <div className="absolute inset-0 bg-radial-blue-glow opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        {realImage ? (
          <img
            src={realImage}
            alt={`${product.name} ${product.strength} research peptide vial — Research Use Only`}
            className="h-full w-full object-contain relative z-10 transition-transform duration-500 group-hover:scale-105"
            data-testid={`product-image-${product.slug}`}
          />
        ) : (
          <ProductVial categorySlug={categorySlug || product.category_id} name={product.name} strength={product.strength} className="h-40 relative z-10 transition-transform duration-500 group-hover:scale-105 group-hover:-translate-y-1" />
        )}
        {!inStock ? (
          <span className="absolute top-2 left-2 z-20 whitespace-nowrap bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm" data-testid={`product-out-of-stock-${product.slug}`}>
            Out of Stock
          </span>
        ) : product.stock_quantity <= 5 ? (
          <span className="absolute top-2 left-2 z-20 whitespace-nowrap bg-amber-500 text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm" data-testid={`product-low-stock-${product.slug}`}>
            Low Stock
          </span>
        ) : null}
        <button
          className="absolute bottom-2 right-2 bg-white/90 backdrop-blur border border-blue-100 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-blue-50 hover:shadow-blue-glow-sm"
          data-testid={`quick-view-btn-${product.slug}`}
          onClick={(e) => e.preventDefault()}
        >
          <Eye className="h-4 w-4 text-brandblue-600" />
        </button>
      </div>

      <h3 className="font-display font-semibold text-slate-900 text-base group-hover:text-brandblue-700 transition-colors" data-testid={`product-name-${product.slug}`}>{product.name}</h3>
      <p className="text-xs text-slate-500 mb-2">{product.strength}</p>

      <div className="flex items-center gap-3 text-[11px] text-slate-500 mb-3">
        <span data-testid={`product-batch-${product.slug}`}>Batch {product.coa?.batch_number}</span>
        <span className="inline-flex items-center gap-1 text-brandblue-600 font-semibold">
          <ShieldCheck className="h-3 w-3" /> Lab Tested
        </span>
        {!inStock ? (
          <span className="font-semibold text-slate-700" data-testid={`product-stock-note-${product.slug}`}>Out of Stock</span>
        ) : product.stock_quantity <= 5 ? (
          <span className="font-semibold text-amber-600" data-testid={`product-stock-note-${product.slug}`}>Low Stock</span>
        ) : null}
      </div>

      <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-100">
        {showQty && inStock ? (
          <div className="flex items-center justify-between w-full gap-2" data-testid={`qty-picker-${product.slug}`}>
            <div className="flex items-center gap-1">
              <button onClick={(e) => changeQty(e, -1)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-600 font-bold hover:bg-slate-50 transition-colors" data-testid={`qty-minus-${product.slug}`}>−</button>
              <span className="w-8 text-center font-display font-bold text-slate-900" data-testid={`qty-value-${product.slug}`}>{qty}</span>
              <button onClick={(e) => changeQty(e, 1)} className="h-8 w-8 rounded-full border border-slate-300 text-slate-600 font-bold hover:bg-slate-50 transition-colors" data-testid={`qty-plus-${product.slug}`}>+</button>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={cancelQty} className="text-xs text-slate-400 hover:text-slate-600 px-1" data-testid={`qty-cancel-${product.slug}`}>✕</button>
              <Button size="sm" className="rounded-full btn-lift" onClick={confirmAdd} data-testid={`qty-confirm-${product.slug}`}>
                Add {qty > 1 ? `${qty} ` : ""}· ${(product.price * qty).toFixed(2)}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <span className="font-display font-bold text-lg text-slate-900" data-testid={`product-price-${product.slug}`}>${product.price.toFixed(2)}</span>
            <Button size="sm" className="rounded-full btn-lift w-24" onClick={openQty} disabled={!inStock} data-testid={`add-to-cart-btn-${product.slug}`}>
              {added ? <Check className="h-3.5 w-3.5" /> : <><ShoppingCart className="h-3.5 w-3.5 mr-1.5" /> Add</>}
            </Button>
          </>
        )}
      </div>
    </Link>
  );
}
